/**
 * ErrorPanel Component
 *
 * Enhanced error display with AI-powered explanations
 */

import { useCallback, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useAIStore, selectHasApiKey } from '../aiStore';
import { explainError } from '../aiService';
import type { EnhancedErrorEntry, ErrorExplanation } from '../types';
import type { ErrorSummary } from '../../simulation/parsers/types';

interface ErrorPanelProps {
  errorSummary: ErrorSummary | null;
  className?: string;
}

/**
 * Severity icon component
 */
function SeverityIcon({ severity }: { severity: 'warning' | 'severe' | 'fatal' }) {
  const iconClass = {
    warning: 'text-yellow-500 dark:text-yellow-400',
    severe: 'text-orange-500 dark:text-orange-400',
    fatal: 'text-red-500 dark:text-red-400',
  }[severity];

  const icon = {
    warning: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    severe: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    fatal: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  }[severity];

  return <span className={iconClass}>{icon}</span>;
}

/**
 * Explanation display component
 */
function ExplanationDisplay({ explanation }: { explanation: ErrorExplanation }) {
  return (
    <div className="mt-3 space-y-3 text-sm">
      {/* Plain English Summary */}
      <div className="p-3 bg-blue-500/10 dark:bg-blue-900/30 rounded-lg border border-blue-300 dark:border-blue-800">
        <div className="flex items-start gap-2">
          <span className="text-blue-500 dark:text-blue-400 mt-0.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <div>
            <span className="font-medium text-blue-600 dark:text-blue-300">Plain English: </span>
            <span className="text-foreground">{explanation.plainEnglishSummary}</span>
          </div>
        </div>
      </div>

      {/* Detailed Explanation */}
      {explanation.detailedExplanation && (
        <div className="text-muted-foreground">
          <span className="font-medium text-foreground">Details: </span>
          {explanation.detailedExplanation}
        </div>
      )}

      {/* Possible Causes */}
      {explanation.possibleCauses.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-foreground font-medium mb-1">
            <svg className="w-4 h-4 text-yellow-500 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Why this happens:
          </div>
          <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-1">
            {explanation.possibleCauses.map((cause, i) => (
              <li key={i}>{cause}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggested Fixes */}
      {explanation.suggestedFixes.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-foreground font-medium mb-2">
            <svg className="w-4 h-4 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            How to fix:
          </div>
          <div className="space-y-2">
            {explanation.suggestedFixes.map((fix, i) => (
              <div
                key={i}
                className={cn(
                  'p-2 rounded border',
                  fix.confidence === 'high' && 'bg-green-500/10 dark:bg-green-900/20 border-green-300 dark:border-green-800',
                  fix.confidence === 'medium' && 'bg-yellow-500/10 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800',
                  fix.confidence === 'low' && 'bg-secondary border-border'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-foreground">{fix.description}</span>
                  <span
                    className={cn(
                      'text-xs px-1.5 py-0.5 rounded whitespace-nowrap',
                      fix.confidence === 'high' && 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200',
                      fix.confidence === 'medium' && 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200',
                      fix.confidence === 'low' && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {fix.confidence}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Documentation */}
      {explanation.relatedDocumentation.length > 0 && (
        <div className="text-muted-foreground text-xs">
          <span className="font-medium">Learn more: </span>
          {explanation.relatedDocumentation.join(', ')}
        </div>
      )}
    </div>
  );
}

/**
 * Individual error item component
 */
function ErrorItem({ error }: { error: EnhancedErrorEntry }) {
  const config = useAIStore((state) => state.config);
  const hasApiKey = useAIStore(selectHasApiKey);
  const updateErrorExplanation = useAIStore((state) => state.updateErrorExplanation);
  const selectedErrorId = useAIStore((state) => state.selectedErrorId);
  const selectError = useAIStore((state) => state.selectError);

  const isSelected = selectedErrorId === error.id;
  const isExpanded = isSelected;

  const handleExplain = useCallback(async () => {
    if (error.isExplaining || error.explanation) return;

    updateErrorExplanation(error.id, { isExplaining: true });

    try {
      const explanation = await explainError(
        {
          errorText: error.message,
          errorContext: error.context,
          severity: error.severity,
        },
        config.apiKey,
        config.useServerProxy
      );

      updateErrorExplanation(error.id, {
        explanation,
        isExplaining: false,
      });
    } catch (err) {
      updateErrorExplanation(error.id, {
        isExplaining: false,
        explanationError: err instanceof Error ? err.message : 'Failed to get explanation',
      });
    }
  }, [error, config, updateErrorExplanation]);

  const handleClick = useCallback(() => {
    selectError(isSelected ? null : error.id);
  }, [error.id, isSelected, selectError]);

  const bgColor = {
    warning: 'bg-yellow-500/10 dark:bg-yellow-900/20 border-yellow-400/50 dark:border-yellow-800/50',
    severe: 'bg-orange-500/10 dark:bg-orange-900/20 border-orange-400/50 dark:border-orange-800/50',
    fatal: 'bg-red-500/10 dark:bg-red-900/20 border-red-400/50 dark:border-red-800/50',
  }[error.severity];

  const selectedBg = {
    warning: 'bg-yellow-500/20 dark:bg-yellow-900/40 border-yellow-500 dark:border-yellow-700',
    severe: 'bg-orange-500/20 dark:bg-orange-900/40 border-orange-500 dark:border-orange-700',
    fatal: 'bg-red-500/20 dark:bg-red-900/40 border-red-500 dark:border-red-700',
  }[error.severity];

  return (
    <div
      className={cn(
        'rounded-lg border transition-all cursor-pointer',
        isSelected ? selectedBg : bgColor,
        'hover:border-opacity-100'
      )}
    >
      {/* Error Header */}
      <div
        className="p-3 flex items-start gap-3"
        onClick={handleClick}
      >
        <SeverityIcon severity={error.severity} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground uppercase text-xs">
              {error.severity}
            </span>
            {error.count && error.count > 1 && (
              <span className="text-xs px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                x{error.count}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 break-words">
            {error.message}
          </p>
          {error.context.length > 0 && !isExpanded && (
            <p className="text-xs text-muted-foreground/70 mt-1 truncate">
              {error.context[0]}
            </p>
          )}
        </div>
        <button
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg
            className={cn('w-5 h-5 transition-transform', isExpanded && 'rotate-180')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-border/50 pt-3">
          {/* Context lines */}
          {error.context.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-muted-foreground mb-1">Context:</div>
              <div className="font-mono text-xs text-muted-foreground bg-secondary/50 p-2 rounded space-y-0.5">
                {error.context.map((ctx, i) => (
                  <div key={i}>{ctx}</div>
                ))}
              </div>
            </div>
          )}

          {/* AI Explanation */}
          {error.explanation ? (
            <ExplanationDisplay explanation={error.explanation} />
          ) : error.isExplaining ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Getting AI explanation...
            </div>
          ) : error.explanationError ? (
            <div className="text-sm text-red-500 dark:text-red-400 py-2">
              Error: {error.explanationError}
              <button
                onClick={handleExplain}
                className="ml-2 underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          ) : hasApiKey ? (
            <button
              onClick={handleExplain}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Explain with AI
            </button>
          ) : (
            <div className="text-sm text-muted-foreground py-2">
              Configure AI settings to get explanations
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main ErrorPanel component
 */
export function ErrorPanel({ errorSummary, className }: ErrorPanelProps) {
  const enhancedErrors = useAIStore((state) => state.enhancedErrors);
  const setEnhancedErrors = useAIStore((state) => state.setEnhancedErrors);
  const clearErrors = useAIStore((state) => state.clearErrors);

  // Convert error summary to enhanced errors when it changes
  useEffect(() => {
    if (!errorSummary) {
      clearErrors();
      return;
    }

    const enhanced: EnhancedErrorEntry[] = errorSummary.entries.map((entry, index) => {
      const base = {
        id: `error-${index}-${entry.severity}-${entry.message.slice(0, 20)}`,
        severity: entry.severity,
        message: entry.message,
        context: entry.context,
      };
      return entry.count !== undefined ? { ...base, count: entry.count } : base;
    });

    setEnhancedErrors(enhanced);
  }, [errorSummary, setEnhancedErrors, clearErrors]);

  if (!errorSummary || enhancedErrors.length === 0) {
    return null;
  }

  const { warningCount, severeCount, fatalCount, simulationSuccessful } = errorSummary;

  // Group errors by severity for display
  const fatalErrors = enhancedErrors.filter((e) => e.severity === 'fatal');
  const severeErrors = enhancedErrors.filter((e) => e.severity === 'severe');
  const warningErrors = enhancedErrors.filter((e) => e.severity === 'warning');

  return (
    <div className={cn('flex flex-col h-full bg-card rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Simulation Errors</h3>
          <div className="flex items-center gap-3 text-sm">
            {fatalCount > 0 && (
              <span className="text-red-500 dark:text-red-400">{fatalCount} fatal</span>
            )}
            {severeCount > 0 && (
              <span className="text-orange-500 dark:text-orange-400">{severeCount} severe</span>
            )}
            {warningCount > 0 && (
              <span className="text-yellow-600 dark:text-yellow-400">{warningCount} warnings</span>
            )}
          </div>
        </div>
        <div
          className={cn(
            'mt-2 text-sm font-medium',
            simulationSuccessful ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
          )}
        >
          {simulationSuccessful ? 'Simulation completed successfully' : 'Simulation failed'}
        </div>
      </div>

      {/* Error List */}
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {/* Fatal errors first */}
        {fatalErrors.map((error) => (
          <ErrorItem key={error.id} error={error} />
        ))}

        {/* Severe errors */}
        {severeErrors.map((error) => (
          <ErrorItem key={error.id} error={error} />
        ))}

        {/* Warnings (limited display) */}
        {warningErrors.slice(0, 20).map((error) => (
          <ErrorItem key={error.id} error={error} />
        ))}

        {warningErrors.length > 20 && (
          <div className="text-sm text-muted-foreground text-center py-2">
            ... and {warningErrors.length - 20} more warnings
          </div>
        )}
      </div>
    </div>
  );
}

export default ErrorPanel;
